import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  TextareaHTMLAttributes,
} from 'react';
import { cx } from '@/lib/utils';

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'ghost' | 'subtle' | 'danger';
  size?: 'sm' | 'md' | 'lg';
};

const BTN_BASE =
  'inline-flex items-center justify-center gap-2 font-semibold rounded-md transition-all duration-150 focus-ring disabled:opacity-50 disabled:cursor-not-allowed select-none active:translate-y-px cursor-pointer';
const BTN_SIZES = {
  sm: 'h-8 px-3 text-[13px]',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-[15px]',
};
const BTN_VARIANTS = {
  primary: 'bg-primary text-primary-foreground hover:opacity-90',
  ghost: 'text-foreground hover:bg-accent hover:text-accent-foreground',
  subtle: 'bg-secondary text-secondary-foreground border border-border hover:border-primary hover:bg-secondary-foreground/10',
  danger: 'bg-destructive/10 text-destructive border border-destructive/30 hover:bg-destructive/20',
};

export function Button({ variant = 'primary', size = 'md', className, ...props }: ButtonProps) {
  return (
    <button
      className={cx(BTN_BASE, BTN_SIZES[size], BTN_VARIANTS[variant], className)}
      {...props}
    />
  );
}

export function Card({
  children,
  className,
  glow,
}: {
  children: ReactNode;
  className?: string;
  glow?: boolean;
}) {
  return <div className={cx('card', glow && 'shadow-lg', className)}>{children}</div>;
}

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={cx(
        'w-full h-11 px-4 rounded-md bg-input border border-border text-foreground placeholder:text-muted-foreground',
        'transition-colors focus:outline-none focus:border-primary focus-ring',
        className,
      )}
      {...props}
    />
  );
}

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className={cx(
        'w-full px-4 py-3 rounded-md bg-input border border-border text-foreground placeholder:text-muted-foreground',
        'transition-colors focus:outline-none focus:border-primary focus-ring resize-y leading-relaxed',
        className,
      )}
      {...props}
    />
  );
}

export function Badge({
  children,
  tone = 'accent',
  className,
  title,
}: {
  children: ReactNode;
  tone?: 'accent' | 'primary' | 'muted' | 'ok' | 'warn' | 'danger';
  className?: string;
  title?: string;
}) {
  const tones = {
    accent: 'bg-accent text-foreground border-border',
    primary: 'bg-primary text-primary-foreground border-transparent',
    muted: 'bg-muted text-muted-foreground border-border',
    ok: 'bg-success/12 text-success border-success/25',
    warn: 'bg-warning/12 text-warning border-warning/25',
    danger: 'bg-destructive/12 text-destructive border-destructive/25',
  };
  return (
    <span
      title={title}
      className={cx(
        'inline-flex items-center gap-1 px-2.5 h-6 rounded-full text-[11px] font-semibold border font-mono',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      className={cx(
        'inline-block w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin',
        className,
      )}
      aria-hidden
    />
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="block mb-1.5 text-[13px] font-semibold text-foreground">{label}</span>
      {children}
      {hint && <span className="block mt-1 text-xs text-muted-foreground">{hint}</span>}
    </label>
  );
}
